import { uploadImageSchema } from '@Containers/UploadImageFormFields/validators';
import {
  EMPTY_FIELD_MESSAGE,
  validateEditorEn,
  validateSpace,
  validateSpaceTextInput,
  validateTextEn,
} from '@Utils/validation';
import { z } from 'zod';

enum STAKEHOLDER_ANALYSIS_MESSAGE {
  DESCRIPTION = 'กรุณาระบุคำอธิบายเพิ่มเติมเรื่องห่วงโซ่ธุรกิจ',
  DESCRIPTION_EN = 'กรุณาระบุคำอธิบายเพิ่มเติมเรื่องห่วงโซ่ธุรกิจเป็นภาษาอังกฤษ',
  INTRODUCTION_EN = 'กรุณาระบุคำบรรยายการวิเคราะห์ผู้มีส่วนได้เสียในห่วงโซ่คุณค่าของธุรกิจเป็นภาษาอังกฤษ',
  REMARK_EN = 'กรุณาระบุหมายเหตุการวิเคราะห์ผู้มีส่วนได้เสียในห่วงโซ่คุณค่าของธุรกิจเป็นภาษาอังกฤษ',
  EXPECTATION_DESCRIPTION_EN = 'กรุณาระบุความคาดหวังของผู้มีส่วนได้เสียเป็นภาษาอังกฤษ',
  RESPONSE_EXPECTATION_DESCRIPTION_EN = 'กรุณาระบุการตอบสนองต่อความคาดหวังของผู้มีส่วนได้เสียเป็นภาษาอังกฤษ',
  OTHERS = 'กรุณาระบุอื่น ๆ',
  OTHERS_EN = 'กรุณาระบุอื่น ๆ เป็นภาษาอังกฤษ',
  EXPECTATION_DESCRIPTION = 'กรุณาระบุความคาดหวังของผู้มีส่วนได้เสีย',
  ASSETS = 'กรุณาเลือกไฟล์ภาพผู้มีส่วนได้เสียในห่วงโซ่คุณค่าธุรกิจ',
  DESCRIPTION_ASSETS_EN = 'กรุณาระบุชื่อรูปภาพ/หัวข้อ/คำอธิบายเพิ่มเติมเป็นภาษาอังกฤษ',
  STAKEHOLDER_GROUPS = 'กรุณาเพิ่มข้อมูลการวิเคราะห์ผู้มีส่วนได้เสียในห่วงโซ่คุณค่าธุรกิจ',
}

const IUploadImageSchema = uploadImageSchema({
  assets: STAKEHOLDER_ANALYSIS_MESSAGE.ASSETS,
  descriptionAssetsEn: STAKEHOLDER_ANALYSIS_MESSAGE.DESCRIPTION_ASSETS_EN,
});

const otherItemSchema = z
  .object({
    id: z.number().nullable().optional(),
    nameTh: z
      .string()
      .min(1, STAKEHOLDER_ANALYSIS_MESSAGE.OTHERS)
      .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .optional(),
    nameEn: z
      .string()
      .min(1, STAKEHOLDER_ANALYSIS_MESSAGE.OTHERS)
      .regex(validateTextEn, STAKEHOLDER_ANALYSIS_MESSAGE.OTHERS_EN)
      .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .optional(),
  })
  .refine(
    (data) => {
      if (data.nameTh && data.nameTh.trim()) {
        return data.nameEn && data.nameEn.trim();
      }
      return true;
    },
    {
      message: STAKEHOLDER_ANALYSIS_MESSAGE.OTHERS_EN,
      path: ['nameEn'],
    }
  );

export const modalAddStakeholderAnalysisValidationSchema = z.object({
  id: z.number().nullable().optional(),
  stakeholderType: z.string().min(1),
  stakeholders: z.array(z.any()).min(1),
  expectationDescriptionTh: z
    .string()
    .min(1, STAKEHOLDER_ANALYSIS_MESSAGE.EXPECTATION_DESCRIPTION)
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  expectationDescriptionEn: z
    .string()
    .min(1, STAKEHOLDER_ANALYSIS_MESSAGE.EXPECTATION_DESCRIPTION)
    .regex(
      validateTextEn,
      STAKEHOLDER_ANALYSIS_MESSAGE.EXPECTATION_DESCRIPTION_EN
    )
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  otherStakeholders: z.array(otherItemSchema).nullable().optional(),
  communicationActivities: z.array(z.any()).nullable().optional(),
  otherCommunicationActivities: z.array(otherItemSchema).nullable().optional(),
  respondingToExpectationTh: z.string().nullable().optional(),
  respondingToExpectationEn: z
    .string()
    .regex(
      validateTextEn,
      STAKEHOLDER_ANALYSIS_MESSAGE.RESPONSE_EXPECTATION_DESCRIPTION_EN
    )
    .nullable()
    .optional(),
});

export const StakeholderAnalysisSchema = z.object({
  introduction: z.object({
    descriptionTh: z
      .string()
      .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
    descriptionEn: z
      .string()
      .regex(validateEditorEn, STAKEHOLDER_ANALYSIS_MESSAGE.INTRODUCTION_EN)
      .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
  }),
  remark: z.object({
    descriptionTh: z
      .string()
      .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
    descriptionEn: z
      .string()
      .regex(validateTextEn, STAKEHOLDER_ANALYSIS_MESSAGE.REMARK_EN)
      .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
  }),
  additionalDescriptionTh: z
    .string({
      message: STAKEHOLDER_ANALYSIS_MESSAGE.DESCRIPTION,
    })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
  additionalDescriptionEn: z
    .string({
      message: STAKEHOLDER_ANALYSIS_MESSAGE.DESCRIPTION,
    })
    .regex(validateEditorEn, STAKEHOLDER_ANALYSIS_MESSAGE.DESCRIPTION_EN)
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
  assets: z.array(IUploadImageSchema),
  stakeholderGroups: z
    .array(modalAddStakeholderAnalysisValidationSchema, {
      message: STAKEHOLDER_ANALYSIS_MESSAGE.STAKEHOLDER_GROUPS,
    })
    .nonempty({
      message: STAKEHOLDER_ANALYSIS_MESSAGE.STAKEHOLDER_GROUPS,
    }),
});
